import { useState, useEffect, useCallback } from "react";
import { TextInput } from "@mantine/core";
import MGAAPI from "../../../api/index";
import { useASelectedCountry } from "../../../hooks/useASelectedCountry";

const MGA_API = new MGAAPI();
const BatteryIncentives = ({ handleInputChange, form }) => {
  const [visibleDetails, setVisibleDetails] = useState({});
  const [incentives, setIncentives] = useState([]);

  const selectedCountry = useASelectedCountry();

  // --- Helper: per-attribute validation (required only if incentive selected) ---
  const getAttributeValidation = useCallback((isActive, inc) => {
    const valueStr = (inc.attributeDefaultValue ?? "").toString().trim();
    const requiredInvalid = isActive && valueStr === ""; // empty string only
    let patternInvalid = false;
    if (isActive && !requiredInvalid && inc.attributeRegex) {
      try {
        const regex = new RegExp(inc.attributeRegex);
        patternInvalid = !regex.test(valueStr);
      } catch (e) {
        // Ignore invalid regex pattern
        patternInvalid = false;
      }
    }
    const isInvalid = requiredInvalid || patternInvalid;
    return {
      isInvalid,
      requiredInvalid,
      patternInvalid,
      message: requiredInvalid
        ? "Value is required"
        : patternInvalid
        ? inc?.attributeRegexMessage || "Value format is invalid"
        : "",
    };
  }, []);

  const toggleDetailVisibility = (id) => {
    setVisibleDetails((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };
  const handleIncentivesInputChange = (incentiveId, attributeId, value) => {
    const updatedData = incentives?.map((item) => {
      if (item.incentiveTemplateId === incentiveId) {
        return {
          ...item,
          incentiveAttributes: item.incentiveAttributes.map((attr) =>
            attr.incentiveTemplateAttributeId === attributeId
              ? { ...attr, attributeDefaultValue: value }
              : attr
          ),
        };
      }
      return item;
    });
    setIncentives(updatedData);
    handleInputChange(updatedData);
  };

  const handleCheckboxChange = (event, incentiveId) => {
    const {
      target: { selected },
    } = { ...event };
    const updatedIncentivesStatus = incentives?.map((item) =>
      item.incentiveTemplateId === incentiveId
        ? { ...item, status: selected }
        : item
    );
    setIncentives(updatedIncentivesStatus);
    handleInputChange(updatedIncentivesStatus);
  };

  useEffect(() => {
    if (form?.values?.incentives?.length) {
      setIncentives(form?.values?.incentives);
    } else {
      const country = selectedCountry || "GEN";
      MGA_API.getPVIncentives("Battery", country).then((res) => {
        setIncentives(res); // Keep API response in local state
      });
    }
    // Re-run when selected country changes or when form changes
  }, [form?.values?.incentives, selectedCountry]);

  // --- Form-level error exposure (aggregated) ---
  const hasIncentiveErrors = useCallback(
    (current = incentives) =>
      current?.some((item) => {
        if (!item?.status) return false;
        return item?.incentiveAttributes?.some((inc) => getAttributeValidation(item.status, inc).isInvalid);
      }),
    [incentives, getAttributeValidation]
  );

  useEffect(() => {
    if (!form) return;
    if (hasIncentiveErrors()) {
      form.setFieldError(
        "batteryIncentives",
        "Please fix Battery Incentive validation errors before saving."
      );
    } else {
      form.clearFieldError("batteryIncentives");
    }
  }, [incentives, form, hasIncentiveErrors]);
  return (
    <div className="col-12 mt-2">
      {/* <div className="col-12">
        <span>Incentives (Regardless of Location)</span>
        {/* <span className="loadSubHeading ml-2">YES</span>

        <label className="switch mt-3">
          <input type="checkbox" checked={true} />
          <span className="slider round ml-2"></span>
        </label>
      </div> */}
      <div className="p-2" style={{pointerEvents:"auto"}}>
        <se-block height="fit-content">
          <se-table>
            <se-table-group-header>
              <se-table-item-header width="60px"></se-table-item-header>
              <se-table-item-header width="53px"></se-table-item-header>
              <se-table-item-header
                resizable="true"
                flex="1"
                max-width="200px"
                sort="asc"
              >
                Incentive Type
              </se-table-item-header>
              <se-table-item-header
                flex="3"
                // max-width="600px"
                clickable="true"
                resizable="true"
              >
                Incentive Description
              </se-table-item-header>
              {/* <se-table-item-header flex="1" min-width="100px" resizable="true">
                Status
              </se-table-item-header> */}
            </se-table-group-header>
            {incentives?.map((item) => (
              <se-table-group selectable="true" key={item.incentiveTemplateId}>
                <se-table-item width="53px">
                  <se-icon
                    onClick={() => toggleDetailVisibility(item.incentiveTemplateId)}
                    option="button"
                    role="button"
                    tabIndex="0"
                    aria-expanded={Boolean(item?.status || visibleDetails[item.incentiveTemplateId])}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleDetailVisibility(item.incentiveTemplateId);
                      }
                    }}
                  >
                    {item?.status || visibleDetails[item.incentiveTemplateId]
                      ? "arrow2_up"
                      : "arrow2_down"}
                  </se-icon>
                </se-table-item>
                <se-table-item width="60px">
                  <se-checkbox
                    selected={item?.status}
                    onClick={(e) =>
                      handleCheckboxChange(e, item.incentiveTemplateId)
                    }
                  ></se-checkbox>
                </se-table-item>
                <se-table-item max-width="200px">
                  <span className="text-wrap">{item.incentiveName}</span>
                </se-table-item>
                <se-table-item>
                  <span className="text-wrap" max-width="600px">
                    {item.incentiveDescription}
                  </span>
                </se-table-item>
                {/* <se-table-item>
                  <span className="text-wrap">
                    {item?.incentiveComputationFormula || "N/A"}
                  </span>
                </se-table-item> */}
                {(item?.status || visibleDetails[item.incentiveTemplateId]) && (
                  <div slot="detail">
                    {item?.incentiveAttributes?.map((inc) => {
                      const { isInvalid, message } = getAttributeValidation(item?.status, inc);
                      return (
                        <se-table-item key={inc.incentiveTemplateAttributeId}>
                          <div className="row align-items-center">
                            <div className="col-3">
                              <div className="row m-0 p-0">
                                <div className="col-6 px-0 d-flex justify-content-end">
                                  <label
                                    htmlFor={`battery-input-${inc.incentiveTemplateAttributeId}`}
                                    className="col-form-label text-wrap fw-bold se-font-14px-bold"
                                  >
                                    {inc.attributeLabelName}
                                  </label>
                                  <span className="se-mandatory ps-2 pt-2">*</span>
                                </div>
                                <div className="col-6">
                                  <TextInput
                                    id={`battery-input-${inc.incentiveTemplateAttributeId}`}
                                    type="text"
                                    className={!item?.status ? "opacity-50" : ""}
                                    name={inc.attributeLabelName}
                                    placeholder="Add Value"
                                    value={inc.attributeDefaultValue ?? ""}
                                    onChange={(e) =>
                                      handleIncentivesInputChange(
                                        item.incentiveTemplateId,
                                        inc.incentiveTemplateAttributeId,
                                        e.target.value
                                      )
                                    }
                                    disabled={!item?.status}
                                    aria-required={!!item?.status}
                                    required={!!item?.status}
                                    aria-invalid={isInvalid}
                                    aria-describedby={
                                      isInvalid
                                        ? `battery-error-${inc.incentiveTemplateAttributeId}`
                                        : undefined
                                    }
                                    error={isInvalid ? message : null}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="col-6">
                              <span className="text-wrap">
                                {inc.attributeDescription}
                              </span>
                            </div>
                          </div>
                        </se-table-item>
                      );
                    })}
                  </div>
                )}
              </se-table-group>
            ))}
          </se-table>
        </se-block>
      </div>
    </div>
  );
};

export default BatteryIncentives;
